// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useLogin, useMe } from '../../api/auth.js';
import { useSignupOrg } from '../../api/customer-enablement.js';
import { BrandSymbol, BrandWordmark } from '../../components/BrandMark.js';

/**
 * Two-step signup. Step 1 collects org info (name + slug); step 2 the
 * admin user (email + display name + password). On success we
 * auto-log-in the new admin and redirect to /dashboard.
 *
 * Public route — pairs with `POST /api/orgs`. Disabled when
 * `ORG_SIGNUP_ENABLED=false` server-side (403 from the API surfaces as
 * a `signup_disabled` error message here).
 */
export function SignupRoute() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const signup = useSignupOrg();
  const login = useLogin();

  const [step, setStep] = useState<1 | 2>(1);
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');

  if (me) return <Navigate to="/dashboard" replace />;

  const onStep1 = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (orgName.trim().length === 0 || slug.trim().length === 0) return;
    setStep(2);
  };

  const onStep2 = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    signup.mutate(
      {
        name: orgName.trim(),
        slug: slug.trim(),
        admin: { email: email.trim(), password, displayName: displayName.trim() },
      },
      {
        onSuccess: () => {
          // Auto-log-in the new admin. If login fails the user can still
          // sign in manually.
          login.mutate(
            { email: email.trim(), password },
            {
              onSuccess: () => navigate('/dashboard', { replace: true }),
              onError: () => navigate('/login', { replace: true }),
            },
          );
        },
      },
    );
  };

  const orgError = signup.error;

  return (
    <main className="login-main" id="main-content">
      <div className="login-card">
        <div className="login-brand">
          <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
          <BrandWordmark className="brand-wordmark" />
        </div>
        <h1 className="login-title">Create your clinic</h1>
        <p className="muted">
          Step {step} of 2 —{' '}
          {step === 1 ? 'tell us about your clinic' : 'create your admin account'}
        </p>

        {step === 1 ? (
          <form onSubmit={onStep1} aria-label="Organization details">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label htmlFor="signup-org-name">
                <span>Clinic name</span>
                <input
                  id="signup-org-name"
                  name="org-name"
                  type="text"
                  autoComplete="organization"
                  value={orgName}
                  onChange={(e) => setOrgName(e.currentTarget.value)}
                  required
                />
              </label>
              <label htmlFor="signup-org-slug">
                <span>URL slug</span>
                <input
                  id="signup-org-slug"
                  name="org-slug"
                  type="text"
                  pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$"
                  value={slug}
                  onChange={(e) => setSlug(e.currentTarget.value.toLowerCase())}
                  aria-describedby="signup-slug-hint"
                  required
                />
                <span id="signup-slug-hint" className="muted" style={{ fontSize: 12 }}>
                  Lowercase letters, digits, hyphens. Visible in your clinic's URL.
                </span>
              </label>
              <button type="submit">Continue</button>
              <p>
                Already have an account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={onStep2} aria-label="Admin user details">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label htmlFor="signup-admin-name">
                <span>Your name</span>
                <input
                  id="signup-admin-name"
                  name="display-name"
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  required
                />
              </label>
              <label htmlFor="signup-admin-email">
                <span>Email</span>
                <input
                  id="signup-admin-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                />
              </label>
              <label htmlFor="signup-admin-password">
                <span>Password (8+ characters)</span>
                <input
                  id="signup-admin-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                />
              </label>
              {orgError ? (
                <div id="signup-error" className="error-banner" role="alert" aria-live="polite">
                  {orgError.code === 'slug_in_use'
                    ? `The slug "${slug}" is already taken.`
                    : orgError.code === 'email_in_use'
                      ? 'That email is already registered.'
                      : orgError.code === 'signup_disabled'
                        ? 'Signup is currently disabled. Contact your administrator.'
                        : orgError.message}
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setStep(1)} disabled={signup.isPending}>
                  Back
                </button>
                <button type="submit" disabled={signup.isPending}>
                  {signup.isPending ? 'Creating…' : 'Create clinic'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
